import React, { useState, useEffect } from 'react';
import { announcementsApi } from '../../services/api';

function AnnouncementList() {
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', message: '', type: 'info' });
  const [editingId, setEditingId] = useState(null);

  const fetchAnnouncements = async () => {
    setIsLoading(true);
    try {
      const response = await announcementsApi.list(false);
      setAnnouncements(response.data.announcements);
    } catch (err) {
      setError('Fehler beim Laden der Ankündigungen');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchAnnouncements(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await announcementsApi.update(editingId, formData);
      } else {
        await announcementsApi.create(formData);
      }
      setShowForm(false);
      setFormData({ title: '', message: '', type: 'info' });
      setEditingId(null);
      fetchAnnouncements();
    } catch (err) {
      alert('Speichern fehlgeschlagen');
    }
  };

  const handleEdit = (announcement) => {
    setFormData({
      title: announcement.title,
      message: announcement.message,
      type: announcement.type,
    });
    setEditingId(announcement.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ankündigung wirklich löschen?')) return;
    try {
      await announcementsApi.delete(id);
      fetchAnnouncements();
    } catch (err) {
      alert('Löschen fehlgeschlagen');
    }
  };

  const handleToggleActive = async (announcement) => {
    try {
      await announcementsApi.update(announcement.id, { is_active: !announcement.is_active });
      fetchAnnouncements();
    } catch (err) {
      alert('Aktualisierung fehlgeschlagen');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  const getTypeLabel = (type) => {
    const labels = { 'info': 'Info', 'warning': 'Warnung', 'maintenance': 'Wartung' };
    return labels[type] || type;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Ankündigungen</h1>
        <button className="primary-button" onClick={() => { setShowForm(true); setEditingId(null); setFormData({ title: '', message: '', type: 'info' }); }}>
          + Neue Ankündigung
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingId ? 'Ankündigung bearbeiten' : 'Neue Ankündigung'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Titel</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Nachricht</label>
                <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required rows={4} />
              </div>
              <div className="form-group">
                <label>Typ</label>
                <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                  <option value="info">Info</option>
                  <option value="warning">Warnung</option>
                  <option value="maintenance">Wartung</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="button" className="secondary-button" onClick={() => { setShowForm(false); setEditingId(null); }}>Abbrechen</button>
                <button type="submit" className="primary-button">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="loading-container"><div className="loading-spinner"></div></div>
      ) : announcements.length === 0 ? (
        <div className="empty-state">Keine Ankündigungen vorhanden</div>
      ) : (
        <div className="announcements-list">
          {announcements.map((announcement) => (
            <div key={announcement.id} className={`announcement-card ${announcement.type} ${announcement.is_active ? 'active' : 'inactive'}`}>
              <div className="announcement-header">
                <span className={`type-badge ${announcement.type}`}>{getTypeLabel(announcement.type)}</span>
                <span className={`status-badge ${announcement.is_active ? 'active' : 'inactive'}`}>
                  {announcement.is_active ? 'Aktiv' : 'Inaktiv'}
                </span>
              </div>
              <h3>{announcement.title}</h3>
              <p>{announcement.message}</p>
              <div className="announcement-meta">
                <span>Erstellt: {formatDate(announcement.created_at)}</span>
              </div>
              <div className="announcement-actions">
                <button onClick={() => handleToggleActive(announcement)} className="action-btn toggle">
                  {announcement.is_active ? '🔴 Deaktivieren' : '🟢 Aktivieren'}
                </button>
                <button onClick={() => handleEdit(announcement)} className="action-btn edit">✏️ Bearbeiten</button>
                <button onClick={() => handleDelete(announcement.id)} className="action-btn delete">🗑️ Löschen</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AnnouncementList;
